import React from "react";

/**
 * BrandLogosSection — premium-minimal authority section for hardware/appliance
 * brand mentions. Uses elegant typographic wordmarks (no third-party trademark
 * logo files) so the site implies no official partnership / distributorship.
 *
 * Props:
 *   - testid:    string (required)
 *   - eyebrow:   small label above the heading
 *   - heading:   section title
 *   - subtext:   1–2 lines under heading
 *   - categories: [{
 *       label?: string,        // sub-block label (e.g. "Hardware & Accessories")
 *       groups: [{
 *         tier?: string,       // e.g. "Standard" / "Premium"
 *         brands: [{ name, country?, style? }]
 *       }]
 *     }]
 *   - disclaimer: small italic line below grids
 *   - dark: render on the deep backdrop variant
 */

// Per-brand typographic tuning. Kept monochrome so it looks like a refined
// wordmark, not a fake logo. Letter-spacing/weight chosen to roughly match
// each brand's real identity at a glance — without using their actual marks.
const BRAND_STYLE = {
  // Hardware
  Blum:    { tracking: "tracking-[0.18em]", weight: "font-bold",     case: "uppercase" },
  Hettich: { tracking: "tracking-[0.14em]", weight: "font-semibold", case: "uppercase" },
  "Häfele":{ tracking: "tracking-[0.18em]", weight: "font-medium",   case: "uppercase" },
  Grass:   { tracking: "tracking-[0.22em]", weight: "font-bold",     case: "uppercase" },
  Salice:  { tracking: "tracking-[0.20em]", weight: "font-light",    case: "uppercase", italic: true },
  // Appliances · Standard
  Bosch:   { tracking: "tracking-[0.18em]", weight: "font-bold",     case: "uppercase" },
  Siemens: { tracking: "tracking-[0.32em]", weight: "font-semibold", case: "uppercase" },
  Teka:    { tracking: "tracking-[0.20em]", weight: "font-bold",     case: "uppercase" },
  // Appliances · Premium
  Gaggenau:{ tracking: "tracking-[0.30em]", weight: "font-light",    case: "uppercase" },
  Miele:   { tracking: "tracking-[0.36em]", weight: "font-semibold", case: "uppercase" },
  Fhiaba:  { tracking: "tracking-[0.24em]", weight: "font-light",    case: "uppercase", italic: true },
};

function Wordmark({ name, dark }) {
  const s = BRAND_STYLE[name] || { tracking: "tracking-[0.16em]", weight: "font-medium", case: "uppercase" };
  const ink = dark ? "text-[#FAF5EC]" : "text-[#1C110C]";
  return (
    <span
      aria-label={name}
      className={`block ${s.case} ${s.weight} ${s.tracking} ${
        s.italic ? "italic" : ""
      } ${ink} text-base md:text-lg leading-none select-none`}
    >
      {name}
    </span>
  );
}

export default function BrandLogosSection({
  testid,
  eyebrow = "Brands We Work With",
  heading = "Premium Hardware & Appliance Brands We Work With",
  subtext = "We specify and install globally trusted hardware, fittings, and appliances based on each project's design, performance requirements, and client preferences.",
  categories = [],
  disclaimer = "Brand selection may vary based on project scope, availability, client preference, and budget.",
  dark = false,
}) {
  const wrapperBg = dark
    ? "bg-[#1C110C] text-[#FAF5EC] border-y border-[#D4A963]/20"
    : "bg-[#F2E9D8] text-[#1C110C] border-y border-[#6E1F2C]/15";
  const cardBg = dark
    ? "bg-[#FAF5EC]/[0.04] border-[#D4A963]/25 hover:border-[#D4A963]/55"
    : "bg-[#FAF5EC] border-[#6E1F2C]/15 hover:border-[#6E1F2C]/45";
  const subtextColor = dark ? "text-[#EDE1C8]/85" : "text-[#3E2A22]";
  const tierColor = dark ? "text-[#D4A963]" : "text-[#6E1F2C]";
  const labelColor = dark ? "text-[#D4A963]" : "text-[#6E1F2C]";
  const captionColor = dark ? "text-[#EDE1C8]/55" : "text-[#7A6B5F]";
  const disclaimerColor = dark ? "text-[#EDE1C8]/65" : "text-[#7A6B5F]";

  return (
    <section data-testid={testid} className={wrapperBg}>
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <p
          className={`text-[10px] tracking-[0.32em] uppercase mb-5 ${
            dark ? "text-[#D4A963]" : "text-[#6E1F2C]"
          }`}
        >
          {eyebrow}
        </p>
        <h2
          data-testid={`${testid}-heading`}
          className="font-serif-display text-2xl md:text-4xl tracking-tight leading-[1.1] max-w-3xl"
        >
          {heading}
        </h2>
        {subtext && (
          <p
            data-testid={`${testid}-subtext`}
            className={`mt-6 max-w-2xl text-base md:text-[17px] leading-[1.85] font-light ${subtextColor}`}
          >
            {subtext}
          </p>
        )}

        <div className="mt-14 md:mt-16 space-y-14">
          {categories.map((cat, ci) => (
            <div key={`${testid}-cat-${ci}`} data-testid={`${testid}-category-${ci}`}>
              {cat.label && (
                <p
                  className={`text-[10px] tracking-[0.36em] uppercase mb-7 ${labelColor}`}
                >
                  {cat.label}
                </p>
              )}
              <div className="space-y-10">
                {cat.groups.map((g, gi) => (
                  <div key={`${testid}-${ci}-${gi}`}>
                    {g.tier && (
                      <p
                        data-testid={`${testid}-tier-${ci}-${gi}`}
                        className={`text-[10px] tracking-[0.36em] uppercase mb-5 ${tierColor}`}
                      >
                        {g.tier}
                      </p>
                    )}
                    <div
                      data-testid={`${testid}-grid-${ci}-${gi}`}
                      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4"
                    >
                      {g.brands.map((b, bi) => (
                        <div
                          key={b.name}
                          data-testid={`${testid}-brand-${ci}-${gi}-${bi}`}
                          aria-label={`${b.name} brand logo`}
                          role="img"
                          className={`${cardBg} border h-[112px] md:h-[124px] flex flex-col items-center justify-center px-4 transition-colors`}
                        >
                          <Wordmark name={b.name} dark={dark} />
                          {b.country && (
                            <span
                              className={`mt-3 text-[9px] tracking-[0.32em] uppercase ${captionColor}`}
                            >
                              {b.country}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {disclaimer && (
          <p
            data-testid={`${testid}-disclaimer`}
            className={`mt-12 max-w-2xl text-sm leading-[1.75] italic ${disclaimerColor}`}
          >
            {disclaimer}
          </p>
        )}
      </div>
    </section>
  );
}
